import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, Subject, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { StringIgnoreCaseComparator } from 'src/app/shared/utils/comparators';
import { Workspace } from '../models/workspace/workspace';
import { WorkspaceWrite } from '../models/workspace/workspaceWrite';
import { BaseService } from './base.service';
import { ApiVersions } from './api-version';
import _ from 'lodash';
import { CurrentWorkspaces } from '../models/workspace/currentWorkspaces';
import { WorkspaceSet } from '../models/workspace/workspaceSet';
import { SystemUserWorkspace } from '../models/system-user/system-user-workspace';
import { PersonWorkspace } from '../models/workspace/personWorkspace';
import { AttributeDefinitionWorkspace } from '../models/workspace/attributeDefinitionWorkspace';
import { AttributeDefinition } from '../models/attributes/attributeDefinition';
import { PersonStatus } from 'src/app/people/models/personStatus';
import { buildFileUploadData } from '../../../shared-app-portal/components/document-upload/document-upload.helper';
import { AttributeService } from './attribute.service';
import { CertificationWorkspace } from '../models/workspace/certificationWorkspace';

@Injectable({
  providedIn: 'root'
})
export class WorkspaceService extends BaseService {
  private basePath = 'workspaces';

  protected _workspaces: BehaviorSubject<Workspace[]> = new BehaviorSubject<Workspace[]>(null);

  public get workspaces(): Observable<Workspace[]> {
    return this._workspaces.asObservable();
  }

  public readonly activeWorkspaceIsOrg: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    null
  );
  public activeWorkspaceIsOrg$ = this.activeWorkspaceIsOrg.asObservable();

  public readonly personWorkspaces: BehaviorSubject<PersonWorkspace[]> = new BehaviorSubject<
    PersonWorkspace[]
  >(null);
  protected _orgHasWorkspaces: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public get orgHasWorkspaces(): Observable<boolean> {
    this._currentWorkspaces.subscribe((workspace) => {
      this._orgHasWorkspaces.next(
        Boolean(
          workspace &&
          workspace.workspaces &&
          ((workspace.workspaces.length == 1 && workspace.workspaces[0].id) ||
            workspace.workspaces.length > 1)
        )
      );
    });
    return this._orgHasWorkspaces.asObservable();
  }

  protected _currentWorkspaces: BehaviorSubject<CurrentWorkspaces> =
    new BehaviorSubject<CurrentWorkspaces>(undefined);

  public get currentWorkspaces(): Observable<CurrentWorkspaces> {
    return this._currentWorkspaces.asObservable();
  }

  public readonly activeWorkspaceLogoUrl: BehaviorSubject<string> = new BehaviorSubject<string>(
    null
  );

  public workspaceDeleted = new Subject<void>();
  constructor(private attributeService: AttributeService) {
    super();
  }

  getWorkspaces() {
    let obs = this.get(this.basePath, null, ApiVersions.unversioned).pipe(
      map((data) => data.map((ws: Workspace) => Workspace.parse(ws)))
    );
    obs.subscribe((data) => {
      this._workspaces.next(data.sort(StringIgnoreCaseComparator('name')));
    });
  }

  getCurrentWorkspaces(): Observable<CurrentWorkspaces> {
    let path = `${this.basePath}/current`;
    let obs = this.get(path, null, ApiVersions.unversioned).pipe(
      map((data: CurrentWorkspaces) => {
        if (data && data.activeWorkspace) {
          data.activeWorkspace = Workspace.parse(data.activeWorkspace);
          this.activeWorkspaceIsOrg.next(data.activeWorkspace.id == null);
        }
        if (data && data.workspaces) {
          data.workspaces = data.workspaces.map((ws: Workspace) => Workspace.parse(ws));
        }
        return data;
      })
    );
    obs.subscribe((data) => {
      if (!data) {
        this.activeWorkspaceIsOrg.next(true);
        this._currentWorkspaces.next(null);
      } else {
        this._currentWorkspaces.next(data);
        this.activeWorkspaceLogoUrl.next(data.activeWorkspace.workspaceLogoUrl);
      }
    });
    return obs;
  }

  isCurrentWorkspace(name: string): boolean {
    if (!name) return true; //This is the org workspace
    const active = this._currentWorkspaces.value?.activeWorkspace;
    return active?.name?.toLowerCase() === name?.toLowerCase();
  }

  getPersonWorkspaces(personId: string) {
    let id = encodeURI(personId);
    let path = `${this.basePath}/person/${id}`;
    this.get(path, null, ApiVersions.unversioned).subscribe((data) => {
      if (data) {
        this.personWorkspaces.next(data);
      }
    });
  }

  assignPersonWorkspace(personId: string, workspaceId: string, status: PersonStatus) {
    if (!this.authService.ownerLevelAccess) return;
    let personIdEncoded = encodeURI(personId);
    let workspaceIdEncoded = encodeURI(workspaceId);
    let body = JSON.stringify(status);
    let path = `${this.basePath}/${workspaceIdEncoded}/assign-person/${personIdEncoded}`;
    return this.put(path, body, null, ApiVersions.unversioned);
  }

  unassignPersonWorkspace(personId: string, workspaceId: string) {
    if (!this.authService.adminLevelAccess) return;
    let personIdEncoded = encodeURI(personId);
    let workspaceIdEncoded = encodeURI(workspaceId);
    let path = `${this.basePath}/${workspaceIdEncoded}/unassign-person/${personIdEncoded}`;
    return this.put(path, null, null, ApiVersions.unversioned);
  }

  getAttributeWorkspaces(attributeId: string): Observable<AttributeDefinitionWorkspace[]> {
    let id = encodeURI(attributeId);
    let path = `${this.basePath}/attribute-definition/${id}`;
    return this.get(path, null, ApiVersions.unversioned).pipe(
      map((data: AttributeDefinitionWorkspace[]) => {
        return data;
      })
    );
  }

  getCertificationWorkspaces(certificationId: string): Observable<CertificationWorkspace[]> {
    let id = encodeURI(certificationId);
    let path = `${this.basePath}/certification/${id}`;
    return this.get(path, null, ApiVersions.unversioned).pipe(
      map((data: CertificationWorkspace[]) => {
        return data;
      })
    );
  }

  assignAttributeWorkspace(attributeId: string, workspaceIds: string[]) {
    let aId = encodeURI(attributeId);
    let path = `${this.basePath}/assign-attribute-definition/${aId}`;
    return this.patch(path, workspaceIds, ApiVersions.unversioned).pipe(
      map((data: AttributeDefinition) => {
        this.attributeService.attributeWorkspaceUpdated.next(data);
        return data;
      })
    );
  }

  removeAttributeWorkspace(
    attributeId: string,
    workspaceIds: string[],
    rethrowError: boolean = false
  ) {
    let aId = encodeURI(attributeId);
    let path = `${this.basePath}/remove-attribute-definition/${aId}`;
    return this.patch(path, workspaceIds, ApiVersions.unversioned, rethrowError).pipe(
      map((data: AttributeDefinition) => {
        this.attributeService.attributeWorkspaceUpdated.next(data);
        return data;
      })
    );
  }

  assignCertificationWorkspace(certificationId: string, workspaceIds: string[]) {
    let aId = encodeURI(certificationId);
    let path = `${this.basePath}/assign-certification/${aId}`;
    return this.patch(path, workspaceIds, ApiVersions.unversioned).pipe(
      map((data: AttributeDefinition) => {
        this.attributeService.attributeWorkspaceUpdated.next(data);
        return data;
      })
    );
  }

  removeCertificationWorkspace(certificationId: string, workspaceIds: string[]) {
    let aId = encodeURI(certificationId);
    let path = `${this.basePath}/remove-certification/${aId}`;
    return this.patch(path, workspaceIds, ApiVersions.unversioned).pipe(
      map((data: AttributeDefinition) => {
        this.attributeService.attributeWorkspaceUpdated.next(data);
        return data;
      })
    );
  }

  setOrgWorkspaceDisplayName(data: SystemUserWorkspace[]) {
    let ws = data.find((x) => x.workspaceId == null);
    ws.workspaceName = 'ORG (Access to all workspaces)';
  }

  getSystemUserWorkspaces(systemUserId: string): Observable<SystemUserWorkspace[]> {
    if (!this.authService.adminLevelAccess) return;
    let id = encodeURI(systemUserId);
    let path = `${this.basePath}/user/${id}`;
    return this.get(path, null, ApiVersions.unversioned).pipe(
      map((data: SystemUserWorkspace[]) => {
        this.setOrgWorkspaceDisplayName(data);
        return data;
      })
    );
  }

  getAvailableWorkspaces(): Observable<SystemUserWorkspace[]> {
    if (!this.authService.adminLevelAccess) return;
    let path = `${this.basePath}/available`;
    return this.get(path, null, ApiVersions.unversioned).pipe(
      map((data: SystemUserWorkspace[]) => {
        this.setOrgWorkspaceDisplayName(data);
        return data;
      })
    );
  }

  create(workspaceWrite: WorkspaceWrite): Observable<Workspace> {
    let body = JSON.stringify(workspaceWrite);
    return this.post(this.basePath, body, null, ApiVersions.unversioned).pipe(
      map((response) => Workspace.parse(response)),
      map((newWorkspace) => {
        this.addAWorkspace(newWorkspace);
        return newWorkspace;
      })
    );
  }

  private addAWorkspace(workspace: Workspace) {
    let workspaces = this._workspaces.value;
    workspaces.push(workspace);
    workspaces.sort(StringIgnoreCaseComparator('name'));
    this._workspaces.next(workspaces);
  }

  update(workspace: Workspace): Observable<Workspace> {
    let path = `${this.basePath}/${workspace.id}`;
    let body = JSON.stringify(workspace);
    return this.put(path, body, null, ApiVersions.unversioned).pipe(
      map((response) => {
        if (response == null || (!response.success && response.error && response.error.length)) {
          this.openSnackBar(response.errorMessages[0]);
          return null;
        } else {
          return response;
        }
      }),
      map((response) => (response ? Workspace.parse(response) : null)),
      map((updatedWorkspace) => {
        if (!updatedWorkspace) return null;
        return updatedWorkspace;
      })
    );
  }

  updateActiveWorkspace(workspace: Workspace, route?: string) {
    let path = `${this.basePath}/set-active`;
    let body = JSON.stringify(new WorkspaceSet(workspace.id));
    return this.post(path, body, null, ApiVersions.unversioned).subscribe(
      () => {
        if (route) {
          location.assign(location.origin.concat(route));
        } else {
          let pathSegments = location.pathname.split('/');
          let baseRoute = location.origin.concat('/', pathSegments[1]);
          location.assign(baseRoute);
        }
      },
      (err) => {
        console.log(err);
      }
    );
  }

  updateSystemUserWorkspaces(systemUserId: string, workspaces: SystemUserWorkspace[]) {
    let id = encodeURI(systemUserId);
    let path = `${this.basePath}/user/${id}`;
    let body = JSON.stringify(workspaces);
    return this.put(path, body, null, ApiVersions.unversioned).subscribe(
      () => {},
      (err) => {
        console.log(err);
      }
    );
  }

  replaceWorkspace(workspace: Workspace): void {
    let workspaces = this._workspaces.value;
    if (!workspaces) {
      this._workspaces.next([workspace]);
      return;
    }
    let updateIndex = workspaces.findIndex((x) => x.id == workspace.id);
    if (updateIndex !== -1) {
      workspaces[updateIndex] = workspace;
    }
    this._workspaces.next(workspaces);
  }

  bulkAdd(workspaceId: string, activePeopleIds: string[], successMessage: string) {
    let workspaceIdEncoded = encodeURI(workspaceId);
    let path = `users/bulk-edit/workspaces/${workspaceIdEncoded}`;

    return this.sendWorkspaceUpdate(path, activePeopleIds, successMessage);
  }

  uploadLogo(file: File, id: string) {
    let path = `${this.basePath}/${id}/current/logo`;

    const formData = buildFileUploadData(file);
    let obs = this.post(path, formData, {}, ApiVersions.unversioned);

    return obs.pipe(
      map((result) => {
        result.filePath = `${result.filePath}?${Date.now()}`;
        return result;
      })
    );
  }

  removeLogo(id: string) {
    let path = `${this.basePath}/${id}/current/logo`;
    return this.delete(path, null, ApiVersions.unversioned);
  }

  deleteWorkspace(workspaceId: string) {
    if (!this.authService.ownerLevelAccess) return;
    let id = encodeURI(workspaceId);
    let path = `${this.basePath}/${id}`;
    return this.delete(path, null, ApiVersions.unversioned);
  }

  private sendWorkspaceUpdate(
    path: string,
    body: any = null,
    successMessage: string = null,
    callback: any = null,
    apiVersion: ApiVersions = ApiVersions.v1_0
  ) {
    let service = this;

    return service.put(path, body, null, apiVersion).subscribe(() => {
      if (successMessage) {
        service.openSnackBar(successMessage, true);
      }
      if (callback) {
        callback();
      }
    });
  }
}
